import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { SORT_ORDER } from '../constants';
import { useProject } from '../context/ProjectContext';

const useFilterResonanceMessages = ({ appliedFilters, sort, size, search, projectId }) => {
  const { actions } = useProject();

  const [isLoading, setIsLoading] = useState(false);
  const [localFilteredMessages, setLocalFilteredMessages] = useState([]);
  const [clusterRespondents, setClusterRespondents] = useState(null);

  const messages = useSelector((state) => state.resonance.messages);

  const updateLocalFilteredMessages = async (appliedFilters) => {
    setIsLoading(true);

    if (!appliedFilters.length) {
      setLocalFilteredMessages(messages);
      setClusterRespondents(null);
      setIsLoading(false);
      return;
    }

    const {
      payload: { filteredMessages, respondents },
    } = await actions('getFilteredMessages', { projectId, appliedFilters });

    setClusterRespondents(respondents);
    setLocalFilteredMessages(filteredMessages);

    setIsLoading(false);
  };

  const sortedMessages = useMemo(() => {
    return sortMessages(localFilteredMessages, sort);
  }, [localFilteredMessages, sort]);

  const searchedMessages = useMemo(() => {
    if (!search) {
      return sortedMessages;
    }

    return searchMessages(sortedMessages, search);
  }, [sortedMessages, search]);

  const resultMessages = useMemo(() => sliceMessages(searchedMessages, size), [searchedMessages, size]);

  useEffect(() => {
    updateLocalFilteredMessages(appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, messages]);

  return { isLoading, messages: resultMessages, clusterRespondents };
};

export default useFilterResonanceMessages;

const sortMessages = (messages, sort) => {
  const { direction, getSortValue } = sort;

  return [...messages].sort((a, b) =>
    direction === SORT_ORDER.DESC ? getSortValue(b) - getSortValue(a) : getSortValue(a) - getSortValue(b),
  );
};

const sliceMessages = (messages, size) => {
  if (!size) {
    return messages;
  }

  return messages.slice(0, size);
};

const searchMessages = (messages, search) => {
  if (!search) {
    return messages;
  }

  const searchedMessages = messages.filter(({ message }) => message?.toLowerCase().includes(search.toLowerCase()));

  return searchedMessages;
};
